import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { useTour } from './TourContext';
import { useSetupTour } from './SetupTourContext';

/** Welcome modal shown before the tour starts */
export function TourWelcomeModal() {
  const { t } = useTranslation();
  const { status, startTour, skipTour } = useTour();

  return (
    <Dialog open={status === 'welcome'} onOpenChange={(open) => { if (!open) skipTour(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-lg">
            {t('tour.welcome.title')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground text-center">
          {t('tour.welcome.subtitle')}
        </p>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button className="w-full" onClick={startTour}>
            {t('tour.welcome.start')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={skipTour}>
            {t('tour.welcome.skip')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Completion modal shown after all tour steps */
export function TourCompletionModal() {
  const { t } = useTranslation();
  const { status, completeTour } = useTour();
  const { isSetupTourDismissed } = useSetupTour();
  const navigate = useNavigate();

  const handleContinueSetup = () => {
    completeTour('continue_setup');
    navigate('/settings?tab=branding&tour=setup');
  };

  return (
    <Dialog open={status === 'completed'} onOpenChange={(open) => { if (!open) completeTour('done'); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <CheckCircle2 className="h-6 w-6 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-lg">
            {t('tour.complete.title')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground text-center">
          {t('tour.complete.subtitle')}
        </p>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {!isSetupTourDismissed() && (
            <Button className="w-full" onClick={handleContinueSetup}>
              {t('tour.complete.continue_setup')}
            </Button>
          )}
          <Button variant="ghost" className="w-full" onClick={() => completeTour('done')}>
            {t('tour.complete.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
